import { Box, Button, CircularProgress, Divider, TextField, Typography } from "@material-ui/core";
import { difference, mapObjIndexed } from "ramda";
import { ChangeEvent, FC, useContext, useEffect, useRef, useState } from "react";

import useDisabledOnLoading from "../../../../hooks/useDisabledOnLoading";
import { applyCreateTimestamp } from "../../../../utils/timestamp";
import { CellManageMode, SetRangeType, SlotsMap } from "../types";
import { getDayName, initialSlots } from "../utils";
import { checkReservedSlots, getSlotsInRange } from "../utils/queries";
import AvailabilitySlots from "../AvailabilitySlots";
import { CellContext } from "../CalendarGrid";
import { CalendarContext } from "../index";
import { CellManageContext } from "./index";
import useDebounce from "../../../../hooks/useDebounce";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import Loader from "../../../../components/Loader";
import { coachAvailabilitiesRangesSubCollRef } from "../../../../utils/referencies";
import { useNotify } from "react-admin";

const CellManageActions: FC = () => {
  const { user } = useCurrentUser();
  const { setTempValue, tempValue, searchValue } = useDebounce({
    defaultValue: 60,
  });
  const notify = useNotify();

  const { selectedCellDate, minSlot, closeModal } = useContext(CellContext);
  const { coachId } = useContext(CalendarContext);
  const { currentDaysSlots, currentDayName } = useContext(CellManageContext);

  const [time, setTime] = useState<number | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);

  const [hasReservedSlots, setHasReservedSlots] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [reservedSlots, setReservedSlots] = useState<any>(null);
  const { disabledStyles } = useDisabledOnLoading(submitLoading);

  const allSlots = useRef<SlotsMap>(new Map(initialSlots));

  const resetAllSlots = () => {
    allSlots.current = new Map(initialSlots);
  };

  const handleSelectedSlots = (slots: Set<number>) => {
    allSlots.current.set(currentDayName, slots);
  };

  const handleSubmit = async () => {
    if (coachId && selectedCellDate) {
      const days = Object.fromEntries(allSlots.current.entries());
      const preparedDays = mapObjIndexed((value: Set<number>) => [...value])(days);

      let data = {
        ...preparedDays,
        startDate: selectedCellDate,
        endDate: selectedCellDate,
        append: false,
        overwrite: true,
        timeZoneOffset: new Date().getTimezoneOffset(),
        duration: +time!,
      };

      const selectedDay = getDayName(selectedCellDate);
      if (reservedSlots && reservedSlots.length) {
        const selctedDaySlots = preparedDays[selectedDay].map((slot: any) => slot / 3600);
        const reservedSlotsHours = reservedSlots.map((slot: any) => new Date(slot.startTime * 1000).getHours());
        const diffSlots = difference(selctedDaySlots, reservedSlotsHours).map((slot) => slot * 3600);

        data = {
          ...preparedDays,
          [selectedDay]: diffSlots,
          startDate: selectedCellDate,
          endDate: selectedCellDate,
          append: false,
          overwrite: true,
          timeZoneOffset: new Date().getTimezoneOffset(),
          duration: +time!,
        };
      }

      console.log(applyCreateTimestamp(data));

      // if mode is `overwrite`, check if there is any reserved slots it the selected range, if so
      // notify user to take action about reserved slots first
      setSubmitLoading(true);

      coachAvailabilitiesRangesSubCollRef(coachId)
        .add(applyCreateTimestamp(data))
        .then(() => {
          setSubmitLoading(false);
          resetAllSlots();
          closeModal();
          notify("Slots have been updated", "success");
        })
        .catch((ex: any) => {
          console.log(ex.message);
          setSubmitLoading(false);
          notify("Error on editing slots", "error");
        });
    }
  };

  const handleChangeSlotTime = (e: ChangeEvent<any>) => {
    const value = e.target.value;
    if (!value || value >= 0) {
      setTempValue(value);
      currentDaysSlots.clear();
    }
  };

  const checkHasReservations = async () => {
    setHasReservedSlots(true);
    try {
      const hasReservedSlots = await checkReservedSlots(
        getSlotsInRange(user.id!, selectedCellDate!, selectedCellDate!),
        CellManageMode.overwrite
      );
      if (hasReservedSlots) {
        setErrorMessage("This range has some reserved slots");
        setHasReservedSlots(true);
        return;
      }
      setErrorMessage(null);
      setHasReservedSlots(false);
    } catch (ex: any) {
      console.log(ex.message);
      setErrorMessage(null);
      setHasReservedSlots(false);
      return;
    }
  };

  const fetchSelectedDaySlots = async () => {
    const snapshots = await getSlotsInRange(user.id!, selectedCellDate!, selectedCellDate!).get();
    if (!snapshots.empty) {
      const doc = snapshots.docs[0].data();
      const docs = snapshots.docs.map((doc: any) => doc.data());
      const filteredDocs = docs.filter((doc) => doc.reserved === true);
      setReservedSlots(filteredDocs);
      setTime(doc.duration);
      setTempValue(doc.duration / 60);
    } else {
      setTime(minSlot!);
    }
    setFetchLoading(false);
  };

  useEffect(() => {
    return () => {
      console.log("Unmount cell manage actions...");
      resetAllSlots();
    };
  }, []);

  useEffect(() => {
    setFetchLoading(true);
    if (selectedCellDate && minSlot) {
      checkHasReservations();
      fetchSelectedDaySlots();
    }
    if (minSlot) setTempValue(minSlot / 60);
  }, [selectedCellDate, minSlot]);

  useEffect(() => {
    if (minSlot) setTime(searchValue ? searchValue * 60 : minSlot);
  }, [searchValue, minSlot]);

  return (
    <Loader loading={fetchLoading}>
      <Box mt={8} style={disabledStyles}>
        <Box mb={2}>
          <Divider />
        </Box>{" "}
        <Box display="flex" justifyContent="space-between">
          <Typography variant="h5">Overwrite Dates</Typography>

          <Box>
            <TextField
              variant="outlined"
              size="small"
              label="Slot time in mintues"
              value={tempValue}
              onChange={handleChangeSlotTime}
              type="number"
              disabled={hasReservedSlots}
              inputProps={{
                min: 1,
              }}
            />
            {hasReservedSlots && errorMessage && (
              <Box mt={1}>
                <Typography variant="body2" color="error">
                  {errorMessage}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {currentDayName && time && selectedCellDate ? (
          <>
            <AvailabilitySlots
              initialDaySlots={allSlots.current.get(currentDayName)}
              minSlot={time}
              getSlots={handleSelectedSlots}
              currentDaySlots={currentDaysSlots.get(selectedCellDate.toLocaleDateString())}
              type={SetRangeType.edit}
              reservedSlots={reservedSlots}
            />
            <Box mt={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                startIcon={submitLoading ? <CircularProgress size={16} style={{ color: "white" }} /> : null}
              >
                Overwrite
              </Button>
            </Box>
          </>
        ) : null}
      </Box>
    </Loader>
  );
};

export default CellManageActions;
